import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, Grid, List, Play, Pause, Edit, Trash2, Copy, MoreVertical, Zap, Clock, CheckCircle, AlertCircle, Users, TrendingUp, Star, User } from 'lucide-react';
import { WorkflowBuilder } from './components/WorkflowBuilder';
import { WorkflowTemplateGallery } from './components/WorkflowTemplateGallery';
import { WorkflowAnalytics } from './components/WorkflowAnalytics';
import { WorkflowExecutionDialog } from './components/WorkflowExecutionDialog';
import { workflowService } from './services/workflowService';
import { Workflow, WorkflowTemplate, WorkflowExecution, WorkflowScope, WorkflowListResponse, WorkflowStatsResponse } from './types/workflow';
import { useToast } from '../../components/ui/Toast';
import { autoSetupDemoAuth } from '../../utils/demoAuth';

interface WorkflowsPageProps {}

export const WorkflowsPage: React.FC<WorkflowsPageProps> = () => {
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<'workflows' | 'templates' | 'analytics'>('workflows');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [showExecutionDialog, setShowExecutionDialog] = useState(false);
  const [executingWorkflow, setExecutingWorkflow] = useState<Workflow | null>(null);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [executions, setExecutions] = useState<WorkflowExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<WorkflowStatsResponse>({
    totalWorkflows: 0,
    activeWorkflows: 0,
    totalExecutions: 0,
    successRate: 0,
    scope: 'personal'
  });
  
  // New state for enhanced functionality
  const [scope, setScope] = useState<WorkflowScope>('personal');
  const [showStarredOnly, setShowStarredOnly] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    // Auto-setup demo authentication if no token exists
    autoSetupDemoAuth();
    loadData();
  }, []);

  useEffect(() => {
    loadData();
  }, [scope, showStarredOnly]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [workflowsResponse, templatesData, executionsData, statsData] = await Promise.all([
        workflowService.getWorkflows(pagination.page, pagination.limit, searchQuery, scope, showStarredOnly).catch(err => {
          console.error('Failed to fetch workflows:', err);
          return {
            workflows: [],
            pagination: { page: 1, limit: 50, total: 0, pages: 0 },
            scope: 'personal' as WorkflowScope,
            starred: false
          };
        }),
        workflowService.getTemplates().catch(err => {
          console.error('Failed to fetch templates:', err);
          return [];
        }),
        workflowService.getExecutions().catch(err => {
          console.error('Failed to fetch executions:', err);
          return [];
        }),
        workflowService.getStats(scope).catch(err => {
          console.error('Failed to fetch stats:', err);
          return {
            totalWorkflows: 0,
            activeWorkflows: 0,
            totalExecutions: 0,
            successRate: 0,
            scope: 'personal' as WorkflowScope
          };
        })
      ]);
      
      setWorkflows(workflowsResponse.workflows || []);
      setPagination(workflowsResponse.pagination);
      setTemplates(templatesData || []);
      setExecutions(executionsData || []);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load workflow data:', error);
      // Set default values in case of error
      setWorkflows([]);
      setTemplates([]);
      setExecutions([]);
      setStats({
        totalWorkflows: 0,
        activeWorkflows: 0,
        totalExecutions: 0,
        successRate: 0,
        scope: 'personal'
      });
      setPagination({ page: 1, limit: 50, total: 0, pages: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkflow = () => {
    setSelectedWorkflow(null);
    setShowBuilder(true);
  };

  const handleEditWorkflow = (workflow: Workflow) => {
    setSelectedWorkflow(workflow);
    setShowBuilder(true);
  };

  const handleCloseBuilder = () => {
    setShowBuilder(false);
    setSelectedWorkflow(null);
    loadData(); // Refresh data
  };

  const handleExecuteWorkflow = (workflow: Workflow) => {
    setExecutingWorkflow(workflow);
    setShowExecutionDialog(true);
  };

  const handleExecutionComplete = (result: any) => {
    addToast({
      title: 'Workflow Executed',
      message: 'The workflow was executed successfully',
      type: 'success'
    });
    loadData(); // Refresh to show new execution
  };

  const handleCloseExecutionDialog = () => {
    setShowExecutionDialog(false);
    setExecutingWorkflow(null);
  };

  const handleToggleWorkflow = async (workflowId: string, isActive: boolean) => {
    try {
      await workflowService.updateWorkflow(workflowId, { isActive: !isActive });
      addToast({
        title: 'Workflow Updated',
        message: `Workflow ${!isActive ? 'activated' : 'deactivated'} successfully`,
        type: 'success'
      });
      loadData();
    } catch (error) {
      console.error('Failed to toggle workflow:', error);
      addToast({
        title: 'Update Error',
        message: 'Failed to update workflow status',
        type: 'error'
      });
    }
  };

  const handleDeleteWorkflow = async (workflowId: string) => {
    if (window.confirm('Are you sure you want to delete this workflow?')) {
      try {
        await workflowService.deleteWorkflow(workflowId);
        addToast({
          title: 'Workflow Deleted',
          message: 'The workflow was deleted successfully',
          type: 'success'
        });
        loadData();
      } catch (error) {
        console.error('Failed to delete workflow:', error);
        addToast({
          title: 'Delete Error',
          message: 'Failed to delete the workflow',
          type: 'error'
        });
      }
    }
  };

  const handleStarWorkflow = async (workflowId: string) => {
    try {
      const result = await workflowService.toggleStar(workflowId);
      addToast({
        title: result.starred ? 'Workflow Starred' : 'Workflow Unstarred',
        message: result.message,
        type: 'success'
      });
      // Update the workflow in the local state
      setWorkflows(prevWorkflows => 
        prevWorkflows.map(workflow => 
          workflow.id === workflowId 
            ? { ...workflow, is_starred: result.starred }
            : workflow
        )
      );
    } catch (error) {
      console.error('Failed to star/unstar workflow:', error);
      addToast({
        title: 'Star Error',
        message: 'Failed to star/unstar the workflow',
        type: 'error'
      });
    }
  };

  const filteredWorkflows = (workflows || []).filter(workflow =>
    workflow.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    workflow.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (showBuilder) {
    return (
      <WorkflowBuilder
        workflow={selectedWorkflow}
        onClose={handleCloseBuilder}
        onSave={handleCloseBuilder}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Workflow Engine</h1>
              <p className="text-neutral-400">Automate your business processes with powerful workflows</p>
            </div>
            <button
              onClick={handleCreateWorkflow}
              className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-blue-500/25"
            >
              <Plus className="w-5 h-5" />
              Create Workflow
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-neutral-900/50 backdrop-blur-sm border border-neutral-800 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-neutral-400 text-sm">Total Workflows</p>
                  <p className="text-2xl font-bold text-white">{stats.totalWorkflows}</p>
                </div>
                <div className="p-3 bg-blue-500/20 rounded-lg">
                  <Zap className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </div>
            
            <div className="bg-neutral-900/50 backdrop-blur-sm border border-neutral-800 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-neutral-400 text-sm">Active Workflows</p>
                  <p className="text-2xl font-bold text-white">{stats.activeWorkflows}</p>
                </div>
                <div className="p-3 bg-green-500/20 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </div>
            
            <div className="bg-neutral-900/50 backdrop-blur-sm border border-neutral-800 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-neutral-400 text-sm">Total Executions</p>
                  <p className="text-2xl font-bold text-white">{stats.totalExecutions}</p>
                </div>
                <div className="p-3 bg-purple-500/20 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </div>
            
            <div className="bg-neutral-900/50 backdrop-blur-sm border border-neutral-800 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-neutral-400 text-sm">Success Rate</p>
                  <p className="text-2xl font-bold text-white">{stats.successRate}%</p>
                </div>
                <div className="p-3 bg-emerald-500/20 rounded-lg">
                  <Users className="w-6 h-6 text-emerald-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Scope and Filters */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              {/* Scope Toggle */}
              <div className="flex items-center gap-1 bg-neutral-900/50 backdrop-blur-sm border border-neutral-800 rounded-xl p-1">
                <button
                  onClick={() => setScope('personal')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    scope === 'personal'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-neutral-400 hover:text-white hover:bg-neutral-800'
                  }`}
                >
                  <User className="w-4 h-4" />
                  Personal
                </button>
                <button
                  onClick={() => setScope('team')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    scope === 'team'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-neutral-400 hover:text-white hover:bg-neutral-800'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  Team
                </button>
              </div>

              {/* Starred Filter */}
              <button
                onClick={() => setShowStarredOnly(!showStarredOnly)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  showStarredOnly
                    ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    : 'bg-neutral-900/50 border border-neutral-800 text-neutral-400 hover:text-white hover:bg-neutral-800'
                }`}
              >
                <Star className="w-4 h-4" />
                Starred Only
              </button>
            </div>

            <div className="text-sm text-neutral-400">
              {pagination.total} workflow{pagination.total !== 1 ? 's' : ''} • {scope === 'personal' ? 'Personal' : 'Team'} view
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 bg-neutral-900/50 backdrop-blur-sm border border-neutral-800 rounded-xl p-1">
            {[
              { id: 'workflows', label: 'Workflows', icon: Zap },
              { id: 'templates', label: 'Templates', icon: Grid },
              { id: 'analytics', label: 'Analytics', icon: TrendingUp }
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id as any)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  activeTab === id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-neutral-400 hover:text-white hover:bg-neutral-800'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {activeTab === 'workflows' && (
          <div>
            {/* Controls */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search workflows..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 bg-neutral-900/50 border border-neutral-700 rounded-lg text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <button className="flex items-center gap-2 px-4 py-2 bg-neutral-900/50 border border-neutral-700 rounded-lg text-neutral-400 hover:text-white transition-colors">
                  <Filter className="w-4 h-4" />
                  Filter
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-neutral-400 hover:text-white'
                  }`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-neutral-400 hover:text-white'
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Workflows Grid/List */}
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
                {filteredWorkflows.map((workflow) => (
                  <WorkflowCard
                    key={workflow.id}
                    workflow={workflow}
                    viewMode={viewMode}
                    scope={scope}
                    onEdit={() => handleEditWorkflow(workflow)}
                    onExecute={() => handleExecuteWorkflow(workflow)}
                    onToggle={() => handleToggleWorkflow(workflow.id, workflow.isActive)}
                    onDelete={() => handleDeleteWorkflow(workflow.id)}
                    onStar={() => handleStarWorkflow(workflow.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'templates' && (
          <WorkflowTemplateGallery
            templates={templates}
            onUseTemplate={(template) => {
              setSelectedWorkflow({
                ...template,
                id: '',
                isActive: false,
                status: 'draft' as const,
                executionCount: 0,
                successRate: 0,
                createdAt: new Date(),
                updatedAt: new Date()
              } as Workflow);
              setShowBuilder(true);
            }}
          />
        )}

        {activeTab === 'analytics' && (
          <WorkflowAnalytics
            workflows={workflows}
            executions={executions}
          />
        )}
      </div>

      {showExecutionDialog && executingWorkflow && (
        <WorkflowExecutionDialog
          workflowId={executingWorkflow.id}
          workflowName={executingWorkflow.name}
          onClose={handleCloseExecutionDialog}
          onExecute={handleExecutionComplete}
        />
      )}
    </div>
  );
};

interface WorkflowCardProps {
  workflow: Workflow;
  viewMode: 'grid' | 'list';
  scope: WorkflowScope;
  onEdit: () => void;
  onExecute: () => void;
  onToggle: () => void;
  onDelete: () => void;
  onStar: () => void;
}

const WorkflowCard: React.FC<WorkflowCardProps> = ({
  workflow,
  viewMode,
  scope,
  onEdit,
  onExecute,
  onToggle,
  onDelete,
  onStar
}) => {
  const [showMenu, setShowMenu] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-400 bg-green-400/20';
      case 'paused': return 'text-yellow-400 bg-yellow-400/20';
      case 'error': return 'text-red-400 bg-red-400/20';
      default: return 'text-neutral-400 bg-neutral-400/20';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return CheckCircle;
      case 'paused': return Pause;
      case 'error': return AlertCircle;
      default: return Clock;
    }
  };

  const StatusIcon = getStatusIcon(workflow.status);

  if (viewMode === 'list') {
    return (
      <div className="bg-neutral-900/50 backdrop-blur-sm border border-neutral-800 rounded-xl p-6 hover:border-neutral-700 transition-all duration-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-2 rounded-lg ${getStatusColor(workflow.status)}`}>
              <StatusIcon className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-white">{workflow.name}</h3>
                <button
                  onClick={onStar}
                  className={`p-1 rounded transition-colors ${
                    workflow.is_starred
                      ? 'text-yellow-400 hover:text-yellow-300'
                      : 'text-neutral-500 hover:text-yellow-400'
                  }`}
                  title={workflow.is_starred ? 'Unstar workflow' : 'Star workflow'}
                >
                  <Star className={`w-4 h-4 ${workflow.is_starred ? 'fill-current' : ''}`} />
                </button>
              </div>
              <p className="text-neutral-400 text-sm">{workflow.description}</p>
              {scope === 'team' && workflow.creator_email && (
                <p className="text-neutral-500 text-xs mt-1">Created by {workflow.creator_email}</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-neutral-400">Last run</p>
              <p className="text-white">{workflow.lastExecutedAt ? new Date(workflow.lastExecutedAt).toLocaleDateString() : 'Never'}</p>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={onExecute}
                className="p-2 text-green-400 hover:bg-green-400/20 rounded-lg transition-colors"
                title="Execute workflow"
              >
                <Play className="w-4 h-4" />
              </button>
              
              <button
                onClick={onToggle}
                className={`p-2 rounded-lg transition-colors ${
                  workflow.isActive 
                    ? 'text-yellow-400 hover:bg-yellow-400/20' 
                    : 'text-green-400 hover:bg-green-400/20'
                }`}
                title={workflow.isActive ? 'Pause workflow' : 'Activate workflow'}
              >
                {workflow.isActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>
              
              <button
                onClick={onEdit}
                className="p-2 text-blue-400 hover:bg-blue-400/20 rounded-lg transition-colors"
                title="Edit workflow"
              >
                <Edit className="w-4 h-4" />
              </button>
              
              <div className="relative">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-2 text-neutral-400 hover:text-white rounded-lg transition-colors"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
                
                {showMenu && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-neutral-800 border border-neutral-700 rounded-lg shadow-xl z-10">
                    <button
                      onClick={() => {
                        // Handle duplicate
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-neutral-300 hover:bg-neutral-700 flex items-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      Duplicate
                    </button>
                    <button
                      onClick={() => {
                        onDelete();
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-red-400 hover:bg-red-400/20 flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-neutral-900/50 backdrop-blur-sm border border-neutral-800 rounded-xl p-6 hover:border-neutral-700 transition-all duration-200 group">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-lg ${getStatusColor(workflow.status)}`}>
            <StatusIcon className="w-5 h-5" />
          </div>
          <button
            onClick={onStar}
            className={`p-1 rounded transition-colors ${
              workflow.is_starred
                ? 'text-yellow-400 hover:text-yellow-300'
                : 'text-neutral-500 hover:text-yellow-400'
            }`}
            title={workflow.is_starred ? 'Unstar workflow' : 'Star workflow'}
          >
            <Star className={`w-4 h-4 ${workflow.is_starred ? 'fill-current' : ''}`} />
          </button>
        </div>
        
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 text-neutral-400 hover:text-white rounded-lg transition-colors opacity-0 group-hover:opacity-100"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          
          {showMenu && (
            <div className="absolute right-0 top-full mt-2 w-48 bg-neutral-800 border border-neutral-700 rounded-lg shadow-xl z-10">
              <button
                onClick={() => {
                  onEdit();
                  setShowMenu(false);
                }}
                className="w-full text-left px-4 py-2 text-neutral-300 hover:bg-neutral-700 flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={() => {
                  // Handle duplicate
                  setShowMenu(false);
                }}
                className="w-full text-left px-4 py-2 text-neutral-300 hover:bg-neutral-700 flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                Duplicate
              </button>
              <button
                onClick={() => {
                  onDelete();
                  setShowMenu(false);
                }}
                className="w-full text-left px-4 py-2 text-red-400 hover:bg-red-400/20 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
      
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-white mb-2">{workflow.name}</h3>
        <p className="text-neutral-400 text-sm line-clamp-2">{workflow.description}</p>
        {scope === 'team' && workflow.creator_email && (
          <p className="text-neutral-500 text-xs mt-1">Created by {workflow.creator_email}</p>
        )}
      </div>
      
      <div className="flex items-center justify-between text-sm text-neutral-400 mb-4">
        <span>Steps: {workflow.definition?.steps?.length || 0}</span>
        <span>Last run: {workflow.lastExecutedAt ? new Date(workflow.lastExecutedAt).toLocaleDateString() : 'Never'}</span>
      </div>
      
      <div className="flex items-center gap-2">
        <button
          onClick={onExecute}
          className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <Play className="w-4 h-4" />
          Execute
        </button>
        
        <button
          onClick={onToggle}
          className={`px-4 py-2 rounded-lg transition-colors ${
            workflow.isActive 
              ? 'bg-yellow-600 hover:bg-yellow-700 text-white' 
              : 'bg-neutral-700 hover:bg-neutral-600 text-neutral-300'
          }`}
          title={workflow.isActive ? 'Pause workflow' : 'Activate workflow'}
        >
          {workflow.isActive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}; 